import { Component, useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { cloneDeep } from 'lodash';
import { Row, Modal } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '~/hoc/withLocalization';
import GenericForm from '~/components/GenericForm/GenericForm';
import MultiTaskSelector from './MultiTaskSelector';

//utils
import getSchema from '~/library/schemas/extraPayments';
import { extraPaymentWageTypes, CLIENT_RULE_CHANGE_VALIDATORS, MANUAL_EXTRA_PAYMENT_TYPES, payrollIntegrationOptions } from '~/utils/constants';

//assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';
import LoadingSpinner from '../../../../elements/LoadingSpinner';

const defaultUiSchema = {
    'ui:field': 'layout',
    'ui:layout:hideframe': true,
    'ui:layout': [
        {
            name: { xs: 6, sm: 6, className: "mt-3" },
            isActive: { xs: { span: 4, offset: 2 }, sm: 3, className: "mt-3" },
        },
        {
            extraPaymentType: { xs: 4, className: "mt-3" },
            amount: { xs: 4, className: "mt-3" },
            payrollWageType: { xs: 4, className: "mt-3" },
        },
        {
            task: { xs: 4, className: "mt-3" },
            userGroups: { xs: 4, className: "mt-3" },
            employmentTypes: { xs: 4, className: "mt-3" },
        },
        {
            excludeNormalHours: { xs: 4, className: "mt-3" },
        },
        {
            fullDay: { xs: 4, className: "mt-4" },
            fromTime: { xs: 4, className: "mt-3", doShow: ({ formData }) => !MANUAL_EXTRA_PAYMENT_TYPES.includes(formData?.extraPaymentType) },
            toTime: { xs: 4, className: "mt-3", doShow: ({ formData }) => !MANUAL_EXTRA_PAYMENT_TYPES.includes(formData?.extraPaymentType) },
            daysOfWeek: { xs: 12, sm: 9, className: "mt-3" },
        },
        {
            description: { xs: 12, className: "my-2" },
        },
    ],
    name: {
        'ui:placeholder': 'Enter additional payment name',
    },
    isActive: {
        'ui:widget': 'ActiveInactive',
        'ui:options': {
            valueInBool: true
        }
    },
    fullDay: {
        'ui:widget': 'Radio',
    },
    fromTime: {
        'ui:widget': 'InputTime',
        'ui:options': {
            valueInInt: true
        }
    },
    toTime: {
        'ui:widget': 'InputTime',
        'ui:options': {
            valueInInt: true
        }
    },
    daysOfWeek: {
        'ui:widget': 'DaysOfWeek',
        'ui:options': {
            includeCheckAll: true
        }
    },
    userGroups: {
        'ui:widget': 'SimpleMultiSelectWidget',
        'ui:options': {}
    },
    description: {
        'ui:widget': 'textarea',
        'ui:placeholder': 'Add a brief description for additional payment rule'
    },
    task: {
        'ui:widget': 'TasksWidget',
        'ui:options': {
            fromAdditionalPaymentsForm: true,
        }
    }
};

class ExtraPaymentsForm extends Component {
    constructor(props) {
        super(props);
        this.wagetypes = props.commonStore.getWageTypesAccordingToIntegrations(props.commonStore?.config?.integration_details, 'su') || [];
        this._userGroups = props.commonStore.config?.client?.data?.extendedRules?.user_groups?.length ?
            props.commonStore.config?.client?.data?.extendedRules?.user_groups
            : [];
        this.state = {
            schema: getSchema({
                payrollWagetypes: this.wagetypes, code: props.entity?.code,
                isNotDeletable: props.entity.isNotDeletable,
                translate: props.t,
                showManualPointType: props.commonStore?.config?.integration_details?.product === payrollIntegrationOptions.VISMA
                    || props.entity?.extraPaymentType?.value === 'manual_point_amount'
            }),
            dataLoaded: false,
            uiSchema: this.prepareSchema(defaultUiSchema, this._userGroups, this.props.entity.code, this.props.t),
            multipleTasksEnabled: false,
            selectedTasks: [],
            showTaskPicker: false,
            // Tracks the selected wage type so the multi-task widget can be enabled reactively for
            selectedType: props.entity?.extraPaymentType?.value || 'amount',
            // exclude normal hours payment is only for company shift clients and is hidden on urgent call rules, so a stale flag saved otherwise starts off
            excludeNormalHours: !!props.entity.excludeNormalHours && !!props.commonStore?.config?.client?.allow_company_shifts
                && !(props.entity.code && props.entity.code.split && props.entity.code.split('_').includes('urgent')),
        };
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = async () => {
        const { schema } = this.state;
        const { entity, taskStore, clientStore } = this.props;
        const _schema = cloneDeep(schema);
        const newState = { schema: _schema, dataLoaded: true };
        // multiple tasks are only offered for the automatic types and on urgent call rules - a manual rule saved with them opens with one task
        if (entity.isMultiTask && ((entity.code && entity.code.split && entity.code.split('_').includes('urgent'))
            || !MANUAL_EXTRA_PAYMENT_TYPES.includes(entity.extraPaymentType?.value))) {
            newState.multipleTasksEnabled = true;
            if (entity.tasks && entity.tasks.length) {
                newState.selectedTasks = entity.tasks;
            } else {
                const tasks = await clientStore.getAdditionalPaymentTasks(entity.code);
                newState.selectedTasks = tasks || [];
            }
        } else if (entity.task) {
            const { task } = await taskStore.load(entity.task);
            taskStore.prependTaskToLookup({
                id: entity.task,
                name: task?.name || null,
                task_number: task?.task_number || null
            });
        }
        if (entity && (entity.task || entity.isMultiTask || MANUAL_EXTRA_PAYMENT_TYPES.includes(entity.extraPaymentType?.value)) && !entity.isNotDeletable) {
            _schema.properties.userGroups['readOnly'] = true;
            _schema.properties.employmentTypes['readOnly'] = true;
        }
        this.setState(newState);
    }

    validateExtraPayment(formData, errors) {
        if (formData) {
            const { t, existingRules } = this.props;
            const { multipleTasksEnabled, selectedTasks } = this.state;
            if (formData?.name && existingRules.includes(formData.name.replace(/\s/g, '').toLowerCase())) {
                errors.name.addError(t('Name already in use, please add unique name'))
            }
            // Task is mandatory for manual entry types (hourly / points) and when exclude normal hours payment is on
            if (MANUAL_EXTRA_PAYMENT_TYPES.includes(formData?.extraPaymentType) || this.state.excludeNormalHours) {
                const hasTask = multipleTasksEnabled ? selectedTasks.length > 0 : !!formData?.task;
                if (!hasTask) {
                    errors.task.addError(t('Please select task, it is required for this payment type'))
                }
            }
            // task uniqueness across rules is validated server-side in onSave (ADDITIONAL_PAYMENT_TASK_CONFLICT)
            if (!this.state.excludeNormalHours && !formData?.daysOfWeek) {
                errors.daysOfWeek.addError(t('Please select atleast one day!'))
            }
            // full day and start/end time are hidden and not used while exclude normal hours payment is on
            if (!this.state.excludeNormalHours && !formData?.fullDay && !formData?.fromTime && !formData?.toTime) {
                errors.fromTime.addError(t('Please select from time or to time'))
                errors.toTime.addError(t('Please select from time or to time'))
            }
        }
        return errors;
    }

    onChangeHandler(formData) {
        if (formData.task) {
            formData.employmentTypes = 'all';
            formData.userGroups = 'none';
        }
        return formData;
    }

    // component-owned toggle (pattern used by the multi-task toggle in this form):
    handleExcludeNormalHoursChange = (checked) => {
        const { t } = this.props;
        if (checked) {
            confirmAlert({
                title: t('Confirm') + '?',
                message: t('When enabled, only the amount defined in this rule will be paid for these hours, and the normal wage for those hours will not be paid. Do you want to continue?'),
                buttons: [
                    { label: t('Yes'), onClick: () => this.setState({ excludeNormalHours: true }) },
                    { label: t('No'), onClick: () => { } },
                ],
            });
        } else {
            this.setState({ excludeNormalHours: false });
        }
    }

    confirmToSave = (data, entity, values) => {
        const { t, userStore } = this.props;
        const message = entity.length < values.length
            ? 'You have added one or more groups to this Additional Payment. Would you like the system to automatically assign this Additional Payment to all members within the selected group(s)'
            : entity.length && values[0] === 'none'
                ? 'You have removed all groups from this additional payment, would like the system to also remove the additional from member profile'
                : entity.length > values.length
                    ? 'You have removed groups from this additional payment, would like the system to also remove the additional from member profile'
                    : 'You have added one or more groups to this Additional Payment. Would you like the system to automatically assign this Additional Payment to all members within the selected group(s)';
        confirmAlert({
            title: t('Confirm Auto-assign') + '?',
            message: t(message),
            buttons: [
                {
                    label: t('Yes') + ', ' + t('auto-assign'),
                    onClick: async () => {
                        await userStore.updateUserProfileOnBasisOfGroups({ extraPayments: data });
                        this.props.handleUserDataChange(data);
                    },
                },
                {
                    label: t('No') + ' ' + t('Will update Manually'),
                    onClick: () => {
                        this.props.handleUserDataChange(data);
                    },
                }
            ],
        });
    }

    onSave = async (values) => {
        const { t, commonStore } = this.props;
        const { schema, multipleTasksEnabled, selectedTasks } = this.state;

        if (multipleTasksEnabled && selectedTasks.length === 0 && values.isActive) {
            return commonStore.addNotification(t('Please select task, as it required and then submit.'), null, 'warning');
        }

        const data = {
            code: values.code ? values.code : values.name.replace(/\s/g, '').toLowerCase(),
            name: values.name,
            extraPaymentType: extraPaymentWageTypes.find(ele => ele.value === values.extraPaymentType),
            payrollWageType: this.wagetypes.find(ele => ele.value === values.payrollWageType),
            amount: values.amount,
            // an exclude normal hours rule covers the whole timesheet by itself, so it's never a full day rule (those are only added by the employee)
            fullDay: this.state.excludeNormalHours ? false : values.fullDay,
            fromTime: values.fromTime,
            toTime: values.toTime,
            // an exclude normal hours rule applies on every day
            daysOfWeek: this.state.excludeNormalHours ? '0,1,2,3,4,5,6' : values.daysOfWeek,
            task: multipleTasksEnabled ? 0 : values.task,
            isMultiTask: multipleTasksEnabled,
            tasks: multipleTasksEnabled ? selectedTasks : [],
            userGroups: values.userGroups,
            employmentTypes: values.employmentTypes,
            description: values.description?.trim() ? values.description : values.name,
            excludeNormalHours: !!this.state.excludeNormalHours,
            isNotDeletable: !!values.isNotDeletable,
            isActive: !!values.isActive
        }
        if (schema?.required.includes('task') && !multipleTasksEnabled && !values.task && values.isActive) {
            return commonStore.addNotification(t('Please select task, as it required and then submit.'), null, 'warning');
        }

        // Warn if the selected task(s) are already used by another active additional payment rule.
        const taskIds = multipleTasksEnabled
            ? selectedTasks.map(taskObj => taskObj.value)
            : (values.task ? [values.task] : []);
        if (taskIds.length) {
            try {
                const res = await this.props.clientStore.validateClientRuleChange(
                    { changeType: CLIENT_RULE_CHANGE_VALIDATORS.ADDITIONAL_PAYMENT_TASK_CONFLICT, taskIds, code: data.code, excludeNormalHours: data.excludeNormalHours },
                    'additionalPayment'
                );
                if (res && res.hasError) {
                    // A task on an exclude normal hours payment rule can't be shared with any other rule — block the save.
                    if (res.isBlocking) {
                        commonStore.addNotification(res.message || t('This task is already used by another additional payment rule'), null, 'warning');
                        return;
                    }
                    confirmAlert({
                        title: t('Are You Sure'),
                        message: res.message || t('This task is already associated with another additional payment rule. Do you still want to save?'),
                        buttons: [
                            { label: t('Yes'), onClick: () => this.proceedSave(data, values) },
                            { label: t('Cancel'), onClick: () => { } },
                        ],
                    });
                    return;
                }
            } catch (e) {
                console.error(e); // don't block saving if the conflict check fails
            }
        }
        this.proceedSave(data, values);
    }

    proceedSave = (data, values) => {
        const { entity } = this.props;
        if (values.userGroups) {
            const entityGroups = entity?.userGroups ? entity?.userGroups.split(',') : [];
            const valuesGroups = values.userGroups.split(',');
            const checkDiffGroups = entityGroups.length !== valuesGroups.length ||
                !entityGroups.every(element => valuesGroups.includes(element)) ||
                valuesGroups.filter(element => !entityGroups.includes(element)).length > 0;
            if (checkDiffGroups && !values?.task && (valuesGroups[0] !== 'none'))
                this.confirmToSave(data, entityGroups, valuesGroups)
            else
                this.props.handleUserDataChange(data);
        }
        else
            this.props.handleUserDataChange(data);
    }

    getBack = () => {
        if (this.props.getBack)
            this.props.getBack()
    }

    static formatEntity = function (entity) {
        const formattedEntity = {
            ...entity,
            payrollWageType: entity?.payrollWageType?.value || 'null',
            extraPaymentType: entity?.extraPaymentType?.value || 'amount',
            userGroups: entity.userGroups ? entity.userGroups : 'none'
        }
        return formattedEntity;
    }

    onChangeForUserGroup = (selected) => {
        if (selected.find(ele => ele.value === 'none') && selected[0].value === 'none') {
            return selected.filter(ele => ele.value !== 'none');
        }
        if ((selected.find(ele => ele.value === 'none') && selected[0].value !== 'none') || !selected.length) {
            return [{ value: 'none', name: 'No Group' }];
        }
    }

    prepareSchema = function (_schema, _userGroups, code, t) {
        _userGroups.sort((a, b) => a.name.localeCompare(b.name));
        const formattedUserGroup = _userGroups.map(ele => ({ label: ele.name, value: ele.code }))
        formattedUserGroup.unshift({ label: 'No Group', value: 'none' })
        const __schema = cloneDeep(_schema)
        // Exclude normal hours payment is only for company shift clients and does not apply to these default rules or the urgent call ones
        // (urgent payments never apply to a normal timesheet) — hide its row
        if (!this.props.commonStore?.config?.client?.allow_company_shifts || ['weekends', 'emergency_shifts', 'shift_allowance'].includes(code)
            || (code && code.split && code.split('_').includes('urgent'))) {
            __schema['ui:layout'] = __schema['ui:layout'].filter(row => !Object.prototype.hasOwnProperty.call(row, 'excludeNormalHours'));
        }
        __schema.userGroups = {
            'ui:widget': 'SimpleMultiSelectWidget',
            'ui:help': t('Please note that all employees in this group will receive these extra payments'),
            'ui:options': {
                options: formattedUserGroup,
                selectAllOption: false,
                customSelectOnChange: true,
                onChangeForUserGroup: (selected) => this.onChangeForUserGroup(selected),
                overrideStrings: {
                    allItemsAreSelected: t('No Group'),
                    selectSomeItems: t('Select Groups'),
                    search: t('Search'),
                    noOptions: t("No groups are available, you can add them in the control panel under 'Extended Rules' and 'User Groups'")
                }
            }
        }
        __schema.task = {
            'ui:widget': 'TasksWidget',
            'ui:help': t('Only members assigned to this task can access these extras, and only when they choose this task'),
            'ui:options': {
                fromAdditionalPaymentsForm: true,
                insertFirst: { name: t('None'), id: 0 }
            }
        }
        __schema.description = {
            'ui:widget': 'textarea',
            'ui:placeholder': t('Add a brief description for additional payment rule')
        }

        __schema.name = {
            'ui:placeholder': t('Enter additional payment name')
        }

        // Wage Type select with a per-option hover tooltip (descriptions come from the wage-type constants).
        __schema.extraPaymentType = {
            'ui:widget': 'SelectWithTooltipWidget',
            'ui:options': {
                optionTooltips: extraPaymentWageTypes.reduce((acc, w) => {
                    if (w.description) acc[w.value] = t(w.description);
                    return acc;
                }, {})
            }
        }

        __schema.excludeNormalHours = {
            'ui:widget': 'StateSwitch',
            'ui:help': t('When enabled, only the amount defined in this rule is paid for these hours, and the normal wage for those hours is not paid')
        }

        switch (code) {
            case 'weekends': {
                __schema.daysOfWeek = {
                    'ui:widget': 'DaysOfWeek',
                    'ui:options': {
                        includeCheckAll: true
                    },
                    'ui:help': t("Please make sure to keep weekend days same as default holidays under basic rules")
                }
                __schema.name = {
                    'ui:placeholder': t('Enter additional payment name'),
                    'ui:help': t("This is a defualt addtional payment, so few settings will be disabled as they can't be overwritten")
                }
                break;
            }
            case 'emergency_shifts': {
                __schema.name = {
                    'ui:placeholder': t('Enter additional payment name'),
                    'ui:help': t("This is a defualt addtional payment, so few settings will be disabled as they can't be overwritten")
                }
                __schema.userGroups['ui:help'] = t("Assign emergency shift(On-call) ability to employee from schedule");
                __schema.employmentTypes = {
                    'ui:help': t("Assign emergency shift(On-call) ability to employee from schedule")
                }
                __schema.fromTime = {
                    'ui:widget': 'InputTime',
                    'ui:options': {
                        valueInInt: true
                    },
                    'ui:help': t("Please choose time after company hours")
                }
                __schema.toTime = {
                    'ui:widget': 'InputTime',
                    'ui:options': {
                        valueInInt: true
                    },
                    'ui:help': t("Please choose time after company hours")
                }
                __schema.isActive = {
                    'ui:widget': 'ActiveInactive',
                    'ui:options': {
                        valueInBool: true
                    },
                    'ui:help': t('Activate emergency shift(On-call) and assigne to employees with shift from schedule screen')
                }
                break;
            }
            case 'shift_allowance': {
                __schema.name = {
                    'ui:placeholder': t('Enter additional payment name'),
                    'ui:help': t("This is a defualt addtional payment, so few settings will be disabled as they can't be overwritten")
                }
                __schema.userGroups['ui:help'] = t("Assign shift allowance to employee from schedule");
                __schema.employmentTypes = {
                    'ui:help': t("Assign shift allowance to employee from schedule")
                }
                __schema.isActive = {
                    'ui:widget': 'ActiveInactive',
                    'ui:options': {
                        valueInBool: true
                    },
                    'ui:help': t('Activate Shift Allowance and use it from schedule screen')
                }
                break;
            }
            default:
                break;
        }
        return __schema;
    }

    // Schema with user groups / employee type locked — used on multi-task toggle (no form-data change).
    buildSchemaWithTaskReadOnly = (isReadOnly) => {
        const _schema = cloneDeep(this.state.schema);
        if (!this.props.entity.isNotDeletable) {
            _schema.properties.employmentTypes['readOnly'] = isReadOnly;
            _schema.properties.userGroups['readOnly'] = isReadOnly;
        }
        return _schema;
    }

    updateSchema = (allFormData, oldData) => {
        const { schema } = allFormData;
        let _schema = cloneDeep(schema);
        // multiple tasks aren't offered for the manual entry types: switching to one turns it off and drops the picked tasks
        const multipleTasksEnabled = this.state.multipleTasksEnabled && !(MANUAL_EXTRA_PAYMENT_TYPES.includes(allFormData?.formData?.extraPaymentType)
            && !(this.props.entity.code && this.props.entity.code.split && this.props.entity.code.split('_').includes('urgent')));
        if (!allFormData?.formData?.isNotDeletable) {
            // Lock user groups / employee type for task-based rules (single task, multi-task, or manual entry types).
            const taskLinked = !!(allFormData?.formData?.task || multipleTasksEnabled
                || MANUAL_EXTRA_PAYMENT_TYPES.includes(allFormData?.formData?.extraPaymentType));
            _schema.properties.employmentTypes['readOnly'] = taskLinked;
            _schema.properties.userGroups['readOnly'] = taskLinked;
            // Task required for manual entry types, except in multi-task mode (validated via selectedTasks).
            const taskRequired = MANUAL_EXTRA_PAYMENT_TYPES.includes(allFormData?.formData?.extraPaymentType) && !multipleTasksEnabled;
            const hasTaskRequired = _schema.required.includes('task');
            if (taskRequired && !hasTaskRequired) {
                _schema.required.push('task');
            } else if (!taskRequired && hasTaskRequired) {
                _schema.required = _schema.required.filter(field => field !== 'task');
            }
        }
        this.setState({ schema: _schema, selectedType: allFormData?.formData?.extraPaymentType, multipleTasksEnabled, selectedTasks: multipleTasksEnabled ? this.state.selectedTasks : [] })
    }

    render() {
        const { schema, uiSchema, dataLoaded, multipleTasksEnabled, selectedTasks, showTaskPicker, excludeNormalHours } = this.state;
        const { t } = this.props;
        if (!dataLoaded)
            return <LoadingSpinner />;

        const isUrgentCallPayment = !!(this.props.entity.code && this.props.entity.code.split && this.props.entity.code.split('_').includes('urgent'));
        // multiple tasks are offered for the automatic types (not the manual entry ones) and on urgent call rules, never on the other default rules
        const enableMultiTask = isUrgentCallPayment || (!['weekends', 'emergency_shifts', 'shift_allowance'].includes(this.props.entity.code)
            && !MANUAL_EXTRA_PAYMENT_TYPES.includes(this.state.selectedType));

        const baseUiSchema = enableMultiTask ? {
            ...uiSchema,
            task: {
                ...uiSchema.task,
                'ui:widget': 'ExtraPaymentTaskWidget',
                'ui:options': {
                    label: false,
                    fromAdditionalPaymentsForm: true,
                    insertFirst: { name: t('None'), id: 0 },
                    multipleTasksEnabled,
                    selectedTasks,
                    t,
                    onToggle: (checked) => {
                        if (!checked && selectedTasks.length > 0) {
                            confirmAlert({
                                title: t('Confirm') + '?',
                                message: t('Turning off Multi-Task will remove all configured tasks. Are you sure you want to continue?'),
                                buttons: [
                                    {
                                        label: t('Yes'),
                                        onClick: () => this.setState({ multipleTasksEnabled: false, selectedTasks: [], schema: this.buildSchemaWithTaskReadOnly(MANUAL_EXTRA_PAYMENT_TYPES.includes(this.state.selectedType)) }),
                                    },
                                    {
                                        label: t('No'),
                                        onClick: () => { },
                                    },
                                ],
                            });
                        } else {
                            this.setState({ multipleTasksEnabled: checked, selectedTasks: checked ? selectedTasks : [], schema: this.buildSchemaWithTaskReadOnly(checked || MANUAL_EXTRA_PAYMENT_TYPES.includes(this.state.selectedType)) });
                        }
                    },
                    onOpenPicker: () => this.setState({ showTaskPicker: true }),
                },
            },
        } : { ...uiSchema };

        // drive the Exclude Normal Hours switch from component state; confirm lives in the handler.
        // while it's on the rule covers the whole timesheet on every day, so full day, its start and end time and the days are hidden
        const derivedUiSchema = {
            ...baseUiSchema,
            'ui:layout': excludeNormalHours
                ? baseUiSchema['ui:layout'].map(row => row.fromTime ? {
                    ...row,
                    fullDay: { ...row.fullDay, doShow: () => false },
                    fromTime: { ...row.fromTime, doShow: () => false },
                    toTime: { ...row.toTime, doShow: () => false },
                    daysOfWeek: { ...row.daysOfWeek, doShow: () => false },
                } : row)
                : baseUiSchema['ui:layout'],
            excludeNormalHours: {
                ...baseUiSchema.excludeNormalHours,
                'ui:options': {
                    checked: excludeNormalHours,
                    onToggle: this.handleExcludeNormalHoursChange,
                    disabled: !!(schema.properties.excludeNormalHours && schema.properties.excludeNormalHours.readOnly),
                },
            },
        };

        return (
            <div className="w-100">
                <GenericForm
                    entity={ExtraPaymentsForm.formatEntity(this.props.entity)}
                    uiSchema={derivedUiSchema}
                    schema={schema}
                    updateSchema={(allFormData, formData) => this.updateSchema(allFormData, formData)}
                    translationScope="forms.extraPayments"
                    onSave={(values, redirect) => this.onSave(values, redirect)}
                    onChange={(fd) => this.onChangeHandler(fd)}
                    customvalidate={(formData, errors) => this.validateExtraPayment(formData, errors)}
                    listUrl={"/"}
                    goBack={() => this.getBack()}
                />

                <MultiTaskSelector
                    show={showTaskPicker}
                    selectedTasks={selectedTasks}
                    onConfirm={(tasks) => this.setState({ selectedTasks: tasks, showTaskPicker: false })}
                    onClose={() => this.setState({ showTaskPicker: false })}
                />
            </div>
        );
    }
}

const ExtraPaymentsFormWithModal = (props) => {
    const [modalOpen, setModalOpen] = useState(props.modalOpen);
    const [formKey, setFormKey] = useState(0);
    useEffect(() => {
        if (props.modalOpen) {
            setFormKey(k => k + 1);
        }
        setModalOpen(props.modalOpen);
    }, [props.modalOpen])
    const closeModal = function () {
        props.closeModal()
    }
    return (
        <Modal
            className={"modal right fade modal_revised"}
            backdrop="static"
            show={modalOpen}
            onHide={closeModal}
        >
            <Modal.Header>
                <Modal.Title className="w-100 text-center m-0">{props.t('Additional Payments')}</Modal.Title>
                <img
                    src={CloseModalIcon}
                    className="cursor-pointer"
                    alt='close_button'
                    onClick={closeModal}
                />
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <ExtraPaymentsForm
                        key={formKey}
                        entity={props.entity || {}}
                        handleUserDataChange={props.handleUserDataChange}
                        getBack={closeModal}
                        {...props}
                    />
                </Row>
            </Modal.Body>
        </Modal>
    )
}

export default inject('commonStore', 'clientStore', 'userStore', 'taskStore')(withLocalization(observer(ExtraPaymentsFormWithModal)));
